import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  useNavigate,
  useSearchParams,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addPdfDisclaimer } from '../utils/pdfDisclaimer';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  SwipeableDrawer,
  Divider,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  MenuItem,
  Select,
  InputLabel,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  ExpandMore,
  FilterList,
  Edit,
  Delete as DeleteIcon,
  Launch,
  Close,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  SportsBasketball,
  ViewColumn as ViewColumnIcon,
  ViewStream as ViewStreamIcon
} from '@mui/icons-material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useBigBoardTable } from '../BigBoardTableContext';
import ListboxComponent from '../components/ListboxComponent ';
import BigBoardComponents from './BigBoardComponents';
import BigBoardPitch from './BigBoardPitch';
import PlayerTypeToggle from '../components/PlayerTypeToggle';
import { normalizeTypeToggleValue } from '../utils/playerTypes';

const getInitials = (nom, prenom) => {
  const firstInitial = nom ? nom[0] : '';
  const secondInitial = prenom ? prenom[0] : '';
  return (firstInitial + secondInitial).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return '#757575';
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 50%)`;
};

const getContrastColor = (hexcolor) => {
  if (!hexcolor) return '#000000';
  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

const joinWithDash = (...items) => items.filter(Boolean).join(' - ');

const getFormIcon = (forme) => {
  if (!forme) return null;
  switch (forme.toLowerCase()) {
    case 'high':
      return <TrendingUp sx={{ color: '#4caf50', fontSize: 32 }} />;
    case 'low':
      return <TrendingDown sx={{ color: '#f44336', fontSize: 32 }} />;
    case 'stable':
      return <TrendingFlat sx={{ color: '#ff9800', fontSize: 32 }} />;
    default:
      return null;
  }
};

function useOnPop(callback) {
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType === 'POP') {
      callback();
    }
  }, [navigationType, callback]);
}

const addPageTemplate = (doc, clubName, departmentText) => {
  const pageNumber = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Récupérer les infos du club
  const finalClubName = clubName || localStorage.getItem('clubName') || 'Club';
  const clubTheme = JSON.parse(localStorage.getItem('clubTheme') || '{}');
  const primaryColor = clubTheme.primaryColor || '#EA7F00';

  // Convertir couleur hex en RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 234, g: 127, b: 0 };
  };
  const rgb = hexToRgb(primaryColor);

  doc.setDrawColor(primaryColor);
  doc.setLineWidth(2);
  doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
  const bandHeight = 20;
  const bandY = pageHeight - 10 - bandHeight;
  doc.setFillColor(rgb.r, rgb.g, rgb.b);
  doc.rect(10, bandY, pageWidth - 20, bandHeight, 'F');
  const logoSize = 16;
  try {
    const logoY = bandY + (bandHeight - logoSize) / 2;

    // Déterminer les logos selon l'URL de l'application
    const currentUrl = window.location.href.toLowerCase();
    const isKansas = currentUrl.includes('kansas');
    const isMetropolitans = currentUrl.includes('metropolitans');
    const isByers = currentUrl.includes('byers');
    const logoLeft = isKansas ? `${process.env.PUBLIC_URL}/assets/kansas.png`
      : isMetropolitans ? `${process.env.PUBLIC_URL}/assets/metropolitans.png`
      : isByers ? `${process.env.PUBLIC_URL}/assets/byers.png`
      : `${process.env.PUBLIC_URL}/assets/LogoValenciaBasket.png`;
    const logoRight = isKansas ? `${process.env.PUBLIC_URL}/assets/kansas.png`
      : isMetropolitans ? `${process.env.PUBLIC_URL}/assets/metropolitans.png`
      : isByers ? `${process.env.PUBLIC_URL}/assets/byers.png`
      : `${process.env.PUBLIC_URL}/assets/alqueria-del-basket.png`;

    doc.addImage(logoLeft, 'PNG', 15, logoY, logoSize, logoSize);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text(
      `${finalClubName} - ${departmentText || 'SCOUTING DEPARTMENT'}`,
      pageWidth / 2,
      bandY + bandHeight / 2 + 3,
      { align: 'center' }
    );
    doc.addImage(logoRight, 'PNG', pageWidth - 15 - logoSize, logoY, logoSize, logoSize);
    doc.setFontSize(8);
    doc.text(pageNumber.toString(), pageWidth / 2, bandY + bandHeight - 3, {
      align: 'center'
    });
  } catch (error) {
    console.error('Error adding images to PDF page:', error);
  }
  doc.setTextColor(0);
  addPdfDisclaimer(doc);
};

const BigBoardTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const mediaUrl = getMediaBaseUrl();
  const section = process.env.REACT_APP_SECTION;
  const userData = JSON.parse(localStorage.getItem('user'));
  const currentUserId = userData?.id || null;
  const userRole = (userData?.role || '').toLowerCase();
  const [bigBoardTypeFilter, setBigBoardTypeFilter] = useState('all');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [boardToDelete, setBoardToDelete] = useState(null);
  const [players, setPlayers] = useState([]);
  const [users, setUsers] = useState([]);
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const [expandedTags, setExpandedTags] = useState(new Set());
  const [selectedTags, setSelectedTags] = useState([]);
  const [openPistaDialog, setOpenPistaDialog] = useState(false);
  const [pistaBoards, setPistaBoards] = useState([]);
  const [pistaTagName, setPistaTagName] = useState('');

  const {
    data,
    setData,
    loading,
    setLoading,
    error,
    setError,
    selectedPlayer,
    setSelectedPlayer,
    selectedUser,
    setSelectedUser,
    tags,
    setTags,
    refreshData,
    refreshDataIfStale,
    scrollPosition,
    setScrollPosition,
    orientation,
    setOrientation
  } = useBigBoardTable();
  const effectiveOrientation = isMobile ? 'vertical' : orientation;
  const [pendingPlayerId, setPendingPlayerId] = useState(null);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingTagIds, setPendingTagIds] = useState([]);

  const loadBigBoard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/bigboard');
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error al cargar los Big Boards:', err);
      setError(t('bigBoardTable.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const playerId = searchParams.get('playerId');
    const userId = searchParams.get('userId');
    const tagIdsParam = searchParams.get('tagIds');
    const expandedParam = searchParams.get('expanded');
    if (playerId) setPendingPlayerId(parseInt(playerId, 10));
    if (userId) setPendingUserId(parseInt(userId, 10));
    if (tagIdsParam) {
      const tagsToRestore = tagIdsParam.split(',').map((id) => parseInt(id, 10));
      setPendingTagIds(tagsToRestore);
    }
    if (expandedParam) {
      const ids = expandedParam
        .split(',')
        .filter((id) => id.trim() !== '')
        .map((id) => parseInt(id, 10));
      setExpandedTags(new Set(ids));
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [tagsResponse, playersResponse, usersResponse] = await Promise.all([
          api.get('/api/tags'),
          api.get('/api/players/select'),
          api.get('/api/users')
        ]);
        setTags(tagsResponse.data);
        setPlayers(playersResponse.data);
        setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
        if (pendingPlayerId && playersResponse.data.players) {
          const foundP = playersResponse.data.players.find(
            (p) => p.id === pendingPlayerId
          );
          if (foundP) setSelectedPlayer(foundP);
        }
        if (pendingUserId && usersResponse.data) {
          const foundU = usersResponse.data.find((u) => u.id === pendingUserId);
          if (foundU) setSelectedUser(foundU);
        }
        if (pendingTagIds.length > 0 && tagsResponse.data) {
          const foundTags = tagsResponse.data.filter((tag) =>
            pendingTagIds.includes(tag.id)
          );
          if (foundTags.length > 0) setSelectedTags(foundTags);
        }
      } catch (err) {
        console.error('Error al cargar los datos iniciales:', err);
      }
      loadBigBoard();
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    window.addEventListener('focus', refreshDataIfStale);
    return () => {
      window.removeEventListener('focus', refreshDataIfStale);
    };
  }, [refreshDataIfStale]);

  useOnPop(() => {
    if (scrollPosition) {
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
    }
  });

  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) {
        setTimeout(() => {
          window.scrollTo(0, scrollPosition);
        }, 0);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [scrollPosition]);

  const handleEditClick = (boardId) => {
    navigate(`/bigboard/edit/${boardId}`);
  };

  const handleDelete = async (boardId) => {
    try {
      await api.delete(`/api/bigboard/${boardId}`);
      await loadBigBoard();
      setOpenDeleteDialog(false);
      setBoardToDelete(null);
    } catch (err) {
      console.error('Error al eliminar el Big Board:', err);
      setError(t('bigBoardTable.deleteError'));
    }
  };

  const handlePlayerClick = (player) => {
    setScrollPosition(window.scrollY);
    navigate(`/players/${player.id}`);
  };

  const handlePlayerUrlClick = (url, event) => {
    event.stopPropagation();
    window.open(url, '_blank');
  };

  const handleOpenPista = (boards, tagName) => {
  setPistaBoards(boards);
  setPistaTagName(tagName);
  setOpenPistaDialog(true);
};

  const filteredBoards = useMemo(() => {
    if (!data) return [];
    return data.filter((board) => {
      const boardTypeLower = board.type ? board.type.toLowerCase() : '';
      let passTypeCheck = true;
      if (userRole === 'super admin') {
        if (bigBoardTypeFilter === 'pro') passTypeCheck = boardTypeLower === 'pro';
        else if (bigBoardTypeFilter === 'cantera')
          passTypeCheck = boardTypeLower === 'cantera';
      } else if (userRole === 'admin pro') {
        passTypeCheck = boardTypeLower === 'pro';
      } else if (userRole === 'admin cantera') {
        passTypeCheck = boardTypeLower === 'cantera';
      } else if (userRole === 'scout interne cantera' || userRole === 'scout externe cantera') {
        passTypeCheck = boardTypeLower === 'cantera' && board.user.id === currentUserId;
      } else if (userRole === 'entrenador' || userRole === 'pdp' || userRole === 'scout interne' || userRole === 'scout externe') {
        passTypeCheck = board.user.id === currentUserId;
      }
      if (!passTypeCheck) return false;
      const matchesPlayer =
        !selectedPlayer || board.players.some((p) => p.id === selectedPlayer.id);
      const matchesUser = !selectedUser || board.user.id === selectedUser.id;
      const matchesTags =
        selectedTags.length === 0 ||
        board.tags.some((boardTag) =>
          selectedTags.some((st) => st.id === boardTag.id)
        );
      return matchesPlayer && matchesUser && matchesTags;
    });
  }, [
    data,
    selectedPlayer,
    selectedUser,
    selectedTags,
    bigBoardTypeFilter,
    userRole,
    currentUserId
  ]);

  const groupedByTag = useMemo(() => {
    if (!tags) return [];
    const groups = new Map();
    tags.forEach((t) => {
      groups.set(t.id, { tag: t, boards: [] });
    });
    filteredBoards.forEach((board) => {
      board.tags.forEach((t) => {
        if (groups.has(t.id)) {
          groups.get(t.id).boards.push(board);
        }
      });
    });
    return Array.from(groups.values()).filter((g) => g.boards.length > 0);
  }, [tags, filteredBoards]);

  const checkIfTagHasAllPositions = (boards) => {
    // Always return true to show PISTA button for all tags
    return true;
  };

  const updateSearchParams = (newState) => {
    const params = new URLSearchParams(searchParams.toString());
    if (expandedTags.size > 0) {
      params.set('expanded', Array.from(expandedTags).join(','));
    } else {
      params.delete('expanded');
    }
    if (newState.selectedPlayer !== undefined) {
      const val = newState.selectedPlayer ? newState.selectedPlayer.id : '';
      if (val) params.set('playerId', val);
      else params.delete('playerId');
    } else if (selectedPlayer) params.set('playerId', selectedPlayer.id);
    else params.delete('playerId');
    if (newState.selectedUser !== undefined) {
      const val = newState.selectedUser ? newState.selectedUser.id : '';
      if (val) params.set('userId', val);
      else params.delete('userId');
    } else if (selectedUser) params.set('userId', selectedUser.id);
    else params.delete('userId');
    if (newState.selectedTags !== undefined) {
      const tagIds = newState.selectedTags.map((t) => t.id);
      if (tagIds.length > 0) params.set('tagIds', tagIds.join(','));
      else params.delete('tagIds');
    } else if (selectedTags.length > 0)
      params.set(
        'tagIds',
        selectedTags.map((t) => t.id).join(',')
      );
    else params.delete('tagIds');
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    updateSearchParams({});
  }, [selectedPlayer, selectedUser, selectedTags, expandedTags]);

  // Auto-scroll vers l'accordéon ouvert quand vient de PlayerProfile (une seule fois)
  const hasAutoScrolled = useRef(false);
  useEffect(() => {
    if (hasAutoScrolled.current) return;
    const shouldScroll = searchParams.get('expanded') && expandedTags.size > 0 && !loading && data && data.length > 0;

    if (shouldScroll) {
      const scrollToAccordion = () => {
        const firstExpandedId = Array.from(expandedTags)[0];
        const accordionElement = document.getElementById(`accordion-tag-${firstExpandedId}`);

        if (accordionElement) {
          const elementPosition = accordionElement.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition - 100,
            behavior: 'smooth'
          });
          hasAutoScrolled.current = true;
        }
      };

      const timeouts = [100, 500, 1000];
      const timeoutIds = timeouts.map(delay =>
        setTimeout(scrollToAccordion, delay)
      );

      return () => {
        timeoutIds.forEach(id => clearTimeout(id));
      };
    }
  }, [expandedTags, loading, data, searchParams]);

  const generatePDFForTag = async (tag, boards) => {
    try {
      // Récupérer les infos du club
      const clubName = localStorage.getItem('clubName') || 'Club';
      const departmentText = t('pdf.footer.department');
      const clubTheme = JSON.parse(localStorage.getItem('clubTheme') || '{}');
      const primaryColor = clubTheme.primaryColor || '#EA7F00';

      // Convertir couleur hex en RGB pour le tableau
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16)
        ] : [234, 127, 0];
      };
      const headerColor = hexToRgb(primaryColor);

      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const dateStr = `${day}-${month}-${year}`;
      const doc = new jsPDF();
      addPageTemplate(doc, clubName, departmentText);
      let yPos = 20;
      doc.setFontSize(18);
      doc.setTextColor(0);
      doc.text(`Ranking - ${tag.libelle}`, doc.internal.pageSize.width / 2, yPos, {
        align: 'center'
      });
      yPos += 15;
      for (let bIndex = 0; bIndex < boards.length; bIndex += 1) {
        const board = boards[bIndex];
        if (yPos > doc.internal.pageSize.height - 50) {
          doc.addPage();
          addPageTemplate(doc, clubName, departmentText);
          yPos = 20;
        }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(board.libelle, 20, yPos);
        yPos += 8;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(0);
        const playersSorted = board.players.sort((a, b) => a.classement - b.classement);
        const rowDataArray = playersSorted.map((p) => {
          const infos = [];
          if (p.poste) infos.push(p.poste);
          if (p.annee_naissance) {
            // Calculate decimal age if date_naissance exists
            if (p.date_naissance) {
              const now = new Date();
              const birthDate = new Date(p.date_naissance);
              const ageInMilliseconds = now - birthDate;
              const ageInYears = ageInMilliseconds / (365.25 * 24 * 60 * 60 * 1000);
              infos.push(`${p.annee_naissance} (${ageInYears.toFixed(1)})`);
            } else {
              infos.push(p.annee_naissance);
            }
          }
          if (p.taille) infos.push(`${p.taille}cm`);
          if (p.nationalite) {
            const natLabel = t(`createPlayer.nationalities.${p.nationalite}`, p.nationalite);
            const nat = p.passport ? `${natLabel} - ${p.passport}` : natLabel;
            infos.push(nat);
          }

          // Add competitions_detail if available, otherwise fallback to old fields
          if (p.competitions_detail && p.competitions_detail.length > 0) {
            // Group competitions by team
            const equipesMap = new Map();
            p.competitions_detail.forEach(comp => {
              if (!equipesMap.has(comp.equipe_id)) {
                equipesMap.set(comp.equipe_id, {
                  equipe_nom: comp.equipe_nom,
                  competitions: []
                });
              }
              equipesMap.get(comp.equipe_id).competitions.push(comp.competition_nom);
            });

            // Format as: Team1 (Comp1, Comp2)
            const teamsStr = Array.from(equipesMap.values())
              .map(equipe => `${equipe.equipe_nom} (${equipe.competitions.join(', ')})`)
              .join(' - ');

            if (teamsStr) infos.push(teamsStr);
          } else {
            // Fallback to old fields
            if (p.team) infos.push(p.team);
            const leagues = [p.league_1, p.league_2, p.competicion_europa].filter(Boolean);
            if (leagues.length > 0) infos.push(leagues.join(' - '));
          }

          const infoLine = infos.join(' - ');
          return [p.classement, p.nom, infoLine];
        });
        doc.autoTable({
          startY: yPos,
          head: [['#', 'Nombre', 'Infos']],
          body: rowDataArray,
          theme: 'grid',
          headStyles: {
            fillColor: headerColor,
            textColor: [255, 255, 255],
            fontSize: 9,
            fontStyle: 'bold'
          },
          styles: {
            fontSize: 8,
            cellPadding: 2,
            overflow: 'linebreak'
          },
          margin: { left: 15, right: 15, bottom: 30 },
          didDrawPage: () => {
            addPageTemplate(doc, clubName, departmentText);
          }
        });
        yPos = doc.lastAutoTable.finalY + 10;
      }
      doc.save(`${clubName} Ranking - ${tag.libelle} - ${dateStr}.pdf`);
    } catch (err) {
      console.error('Erreur lors de la génération du PDF:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {t('bigBoardTable.noRankings')}
      </Alert>
    );
  }

  const disablePlayerLink = userRole === 'entrenador' || userRole === 'pdp';

  return (
    <>
      <Paper elevation={2} sx={{ overflowX: 'hidden', maxWidth: '100%' }}>
        <Box sx={{ p: 2, pb: isMobile ? 7 : 2, overflowX: 'hidden', minWidth: 0 }}>
          {!isMobile && (
            <>
              {userRole === 'super admin' && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <PlayerTypeToggle
                      value={normalizeTypeToggleValue(bigBoardTypeFilter)}
                      onChange={(val) => setBigBoardTypeFilter(val)}
                      t={t}
                      tPrefix="bigBoardTable"
                      showProspects={false}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    fullWidth
                    options={players.players || []}
                    getOptionLabel={(p) => p?.nom || ''}
                    value={selectedPlayer}
                    onChange={(_, newValue) => {
                      setSelectedPlayer(newValue);
                      updateSearchParams({ selectedPlayer: newValue });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label={t('bigBoardTable.filterByPlayer')} />
                    )}
                    renderOption={(props, player) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Avatar
                          src={`${mediaUrl}/photos/${player.id}.png`}
                          alt={player.nom}
                          sx={{ width: 32, height: 32 }}
                        />
                        <Typography noWrap>{player.nom}</Typography>
                      </Box>
                    )}
                    ListboxComponent={ListboxComponent}
                    clearOnEscape
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    fullWidth
                    options={users}
                    getOptionLabel={(u) => (u ? `${u.prenom} ${u.nom}` : '')}
                    value={selectedUser}
                    onChange={(_, newValue) => {
                      setSelectedUser(newValue);
                      updateSearchParams({ selectedUser: newValue });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label={t('bigBoardTable.filterByUser')} />
                    )}
                    renderOption={(props, user) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <BigBoardComponents.UserAvatar user={user} mediaUrl={mediaUrl} size={32} />
                        <Typography noWrap>{`${user.prenom} ${user.nom}`}</Typography>
                      </Box>
                    )}
                    clearOnEscape
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    multiple
                    fullWidth
                    options={tags}
                    getOptionLabel={(t) => t.libelle}
                    value={selectedTags}
                    onChange={(_, newValue) => {
                      setSelectedTags(newValue);
                      updateSearchParams({ selectedTags: newValue });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label={t('bigBoardTable.filterByTags')} />
                    )}
                    renderOption={(props, t) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Chip
                          size="small"
                          label={t.libelle}
                          style={{
                            backgroundColor: t.code_couleur,
                            color: getContrastColor(t.code_couleur)
                          }}
                        />
                      </Box>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((tag, index) => (
                        <Chip
                          key={tag.id}
                          size="small"
                          label={tag.libelle}
                          {...getTagProps({ index })}
                          style={{
                            backgroundColor: tag.code_couleur,
                            color: getContrastColor(tag.code_couleur)
                          }}
                        />
                      ))
                    }
                    clearOnEscape
                  />
                </Grid>
              </Grid>
            </>
          )}
          <Box
            sx={{
              mx: 2,
              mt: 2,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.1rem'
              }}
            >
              {groupedByTag.length}
            </Box>
            <Typography variant="subtitle1">{t('bigBoardTable.rankingsFound')}</Typography>
            {!isMobile && (
              <Tooltip title={t('bigBoardTable.viewToggleTooltip')}>
                <ToggleButtonGroup
                  value={orientation}
                  exclusive
                  size="small"
                  onChange={(_, value) => {
                    if (value) setOrientation(value);
                  }}
                  sx={{
                    ml: 'auto',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 1,
                    '& .MuiToggleButton-root': {
                      color: 'white',
                      border: 'none',
                      px: 1.25,
                      py: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255,255,255,0.85)',
                        color: theme.palette.primary.main,
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }
                      },
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                    }
                  }}
                >
                  <ToggleButton value="vertical" aria-label={t('bigBoardTable.viewVertical')}>
                    <Tooltip title={t('bigBoardTable.viewVertical')}>
                      <ViewColumnIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="horizontal" aria-label={t('bigBoardTable.viewHorizontal')}>
                    <Tooltip title={t('bigBoardTable.viewHorizontal')}>
                      <ViewStreamIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Tooltip>
            )}
          </Box>
          {groupedByTag.map(({ tag, boards }) => (
            <BigBoardComponents.TagAccordion
              key={tag.id}
              tag={tag}
              boards={boards}
              onEdit={handleEditClick}
              onDelete={(board) => {
                setBoardToDelete(board);
                setOpenDeleteDialog(true);
              }}
              onPlayerClick={handlePlayerClick}
              onPlayerUrlClick={handlePlayerUrlClick}
              mediaUrl={mediaUrl}
              expandedTags={expandedTags}
              setExpandedTags={setExpandedTags}
              disablePlayerLink={disablePlayerLink}
              onGeneratePDFForTag={generatePDFForTag}
              onOpenPista={(boards) => handleOpenPista(boards, tag.libelle)}
              showPistaButton={checkIfTagHasAllPositions(boards)}
              orientation={effectiveOrientation}
            />
          ))}
          {isMobile && (
            <Fab
              color="primary"
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: theme.zIndex.fab
              }}
              onClick={() => setOpenFilterDrawer(true)}
            >
              <FilterList />
            </Fab>
          )}
        </Box>
        <BigBoardComponents.FilterDrawerComponent
          open={openFilterDrawer}
          onClose={() => setOpenFilterDrawer(false)}
          players={players}
          users={users}
          selectedPlayer={selectedPlayer}
          selectedUser={selectedUser}
          onPlayerChange={(_, newValue) => {
            setSelectedPlayer(newValue);
            updateSearchParams({ selectedPlayer: newValue });
          }}
          onUserChange={(_, newValue) => {
            setSelectedUser(newValue);
            updateSearchParams({ selectedUser: newValue });
          }}
          mediaUrl={mediaUrl}
          tags={tags}
          selectedTags={selectedTags}
          onTagsChange={(_, newValue) => {
            setSelectedTags(newValue);
            updateSearchParams({ selectedTags: newValue });
          }}
          userRole={userRole}
          bigBoardTypeFilter={bigBoardTypeFilter}
          setBigBoardTypeFilter={setBigBoardTypeFilter}
        />
        <BigBoardPitch
  open={openPistaDialog}
  onClose={() => setOpenPistaDialog(false)}
  boards={pistaBoards}
  mediaUrl={mediaUrl}
  tagName={pistaTagName}
/>
        <Dialog
          open={openDeleteDialog}
          onClose={() => {
            setOpenDeleteDialog(false);
            setBoardToDelete(null);
          }}
        >
          <DialogTitle>{t('bigBoardTable.confirmDelete')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('bigBoardTable.confirmDeleteMessage', { name: boardToDelete?.libelle })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenDeleteDialog(false);
                setBoardToDelete(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              color="error"
              onClick={() => handleDelete(boardToDelete?.id)}
              variant="contained"
            >
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </>
  );
};

export default BigBoardTable;